import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Analytics } from 'utils/analytics';
import { isFilterSelected, getDefaultSortValue } from 'utils/filters';
import {
  fetchActiveListings,
  fetchPublicActiveListings,
  fetchPublicPreviewListings,
  updateFilter
} from 'components/active-auctions/actions';
import { useForm } from 'react-hook-form';
import { useRouteMatch } from 'react-router-dom';
import { useTokenData } from './useTokenData';
import { DEFAULT_MYAUCTION_LIST_LENGTH } from '../constants/auctions';
import { ROUTE_PATHS } from '../routes';

export const useAuctionFilters = (onlyInPreview, closeFilters, withScrollTop = false) => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm();
  const isMyLots = !!useRouteMatch(ROUTE_PATHS.MY_LOTS);
  const isMyBids = !!useRouteMatch(ROUTE_PATHS.MY_BIDS);

  const { isAuth, accountId, isCanViewListing } = useTokenData();
  const { activeAuctionId, searchDictionaries, chosenFilters, pagination } = useSelector(
    state => state.activeAuction
  );
  const {
    jewelryTypes,
    metals,
    categories,
    gemstones,
    brandsAndDesigners,
    estimationRanges,
    styles
  } = searchDictionaries;
  const isCatalog = CONFIG.AUCTION_TYPE === 'collection';
  const [selectedOptions, setSelectedOptions] = useState({
    ...chosenFilters,
    freeSearchString: chosenFilters.freeSearchString || '',
    jewelryStyles: chosenFilters.jewelryStyles || [],
    jewelryTypes: chosenFilters.jewelryTypes || [],
    brandsAndDesigners: chosenFilters.brandsAndDesigners || [],
    estimateRanges: chosenFilters.estimateRanges || [],
    categories: chosenFilters.categories || [],
    gemstones: chosenFilters.gemstones || [],
    metals: chosenFilters.metals || [],
    pageSize: pagination.numberPerPage || DEFAULT_MYAUCTION_LIST_LENGTH,
    onlyFavorites: false,
    onlyInPreview,
    onlyMyBids: isMyBids,
    auctionId: activeAuctionId
  });
  const formValues = watch();

  useEffect(() => {
    if (activeAuctionId) {
      setSelectedOptions(prevState => {
        return { ...prevState, auctionId: activeAuctionId };
      });
    }
  }, [activeAuctionId]);

  const regex = /[\d|,|.+]+/g;

  const getData = data => {
    if (isAuth && isCanViewListing) {
      dispatch(fetchActiveListings(data, isCatalog, true));
    } else if (chosenFilters.onlyInPreview) {
      dispatch(fetchPublicPreviewListings(data, isCatalog, true));
    } else {
      dispatch(fetchPublicActiveListings(data, isCatalog, true));
    }
  };

  const getRangesOptions = () =>
    estimationRanges
      ? Object.entries(estimationRanges).map(([key]) => {
          const ranges = key.split(' ');
          const firstVal = ranges[0].match(regex);
          const secondVal = ranges.length === 3 ? ranges[2].match(regex) : ranges[1].match(regex);
          let to = Number.parseInt(secondVal) > 0 ? Number.parseInt(secondVal) * 1000 : null;
          if (to === 1000) to = 999;
          return ranges.length > 0
            ? {
                label: `${key}`,
                value: {
                  from: Number.parseInt(firstVal) > 0 ? Number.parseInt(firstVal) * 1000 : 0,
                  to
                }
              }
            : {
                label: `none`,
                value: {
                  from: 0,
                  to: 0
                }
              };
        })
      : [];

  const onSelectedFilter = (option, type) => {
    switch (true) {
      case type === 'prices':
        setValue('estimateLow', '');
        setValue('estimateHigh', '');
        setSelectedOptions({
          ...selectedOptions,
          estimateLow: '',
          estimateHigh: ''
        });
        break;
      case type === 'estimateRanges':
        if (
          selectedOptions.estimateRanges.filter(s => s.from === option.from && s.to === option.to)
            .length > 0
        ) {
          setSelectedOptions({
            ...selectedOptions,
            estimateRanges: selectedOptions.estimateRanges.filter(
              s => s.from !== option.from && s.to !== option.to
            )
          });
        } else {
          setSelectedOptions({
            ...selectedOptions,
            [type]: [...selectedOptions[type], option]
          });
        }
        break;

      case Array.isArray(selectedOptions[type]) && type !== 'estimateRanges':
        if (selectedOptions[type].includes(option)) {
          setSelectedOptions({
            ...selectedOptions,
            [type]: selectedOptions[type].filter(i => i !== option)
          });
        } else {
          setSelectedOptions({
            ...selectedOptions,
            [type]: [...selectedOptions[type], option]
          });
        }
        break;
      default:
        if (selectedOptions[type] === option) {
          setValue(type, '');
          setSelectedOptions({
            ...selectedOptions,
            [type]: ''
          });
        } else {
          setSelectedOptions({
            ...selectedOptions,
            [type]: [...selectedOptions[type], option]
          });
        }
        break;
    }
  };

  const clearFilters = () => {
    const data = {
      onlyMyBids: chosenFilters.onlyMyBids,
      onlyMyListings: chosenFilters.onlyMyListings,
      onlyInPreview: chosenFilters.onlyInPreview,
      onlyFavorites: chosenFilters.onlyFavorites,
      jewelryStyles: [],
      jewelryTypes: [],
      estimateRanges: [],
      brandsAndDesigners: [],
      gemstones: [],
      categories: [],
      metals: [],
      catalogNumber: null,
      freeSearchString: '',
      auctionId: chosenFilters.auctionId,
      pageSize: selectedOptions.pageSize,
      pageNumber: 1,
      sortBy: chosenFilters.sortBy
    };
    reset(data);
    setSelectedOptions(data);
    getData(data);
    dispatch(updateFilter(data));
  };

  useEffect(() => {
    setSelectedOptions({
      ...selectedOptions,
      ...formValues
    });
  }, [
    formValues.freeSearchString,
    formValues.catalogNumber,
    formValues.estimateLow,
    formValues.estimateHigh
  ]);

  useEffect(() => {
    if (
      JSON.stringify(selectedOptions.jewelryTypes) !== JSON.stringify(chosenFilters.jewelryTypes)
    ) {
      setSelectedOptions({
        ...selectedOptions,
        jewelryTypes: chosenFilters.jewelryTypes
      });
    }
  }, [chosenFilters.jewelryTypes]);

  const onSubmit = data => {
    const tempData = {
      ...data,
      onlyMyBids: chosenFilters.onlyMyBids || selectedOptions.onlyMyBids,
      onlyMyListings: chosenFilters.onlyMyListings,
      onlyFavorites: chosenFilters.onlyFavorites,
      onlyInPreview,
      auctionId: chosenFilters.auctionId,
      account_id: accountId,
      jewelryStyles: selectedOptions.jewelryStyles,
      freeSearchString: selectedOptions.freeSearchString,
      catalogNumber: data.catalogNumber,
      jewelryTypes: selectedOptions.jewelryTypes,
      brandsAndDesigners: selectedOptions.brandsAndDesigners,
      estimateRanges: selectedOptions.estimateRanges,
      categories: selectedOptions.categories,
      gemstones: selectedOptions.gemstones,
      metals: selectedOptions.metals,
      sortBy: chosenFilters.sortBy,
      pageSize: selectedOptions.pageSize,
      pageNumber: 1
    };
    const dataToSend = tempData;
    Object.keys(data).forEach(key => {
      if (data[key] === '') {
        dataToSend[key] = null;
      }
    });
    getData(dataToSend);
    if (isAuth) {
      Analytics.track('Search', dataToSend);
    }
    if (closeFilters) {
      closeFilters();
    }
    if (withScrollTop) {
      window.scrollTo(0, 0);
    }
  };

  const sortByHandler = option => {
    getData({
      ...selectedOptions,
      auctionId: window.location.pathname.split('/')[3],
      sortBy: option.value ? [{ ...option.value }] : getDefaultSortValue(isMyLots, isCatalog)
    });
  };

  const onToggleFavorite = onlyFavorites => {
    setSelectedOptions({
      ...selectedOptions,
      onlyFavorites
    });
    getData({
      ...selectedOptions,
      onlyFavorites,
      pageNumber: 1,
      sortBy: getDefaultSortValue(isMyLots, isCatalog)
    });
  };

  const changeItemsPerPage = option => {
    setSelectedOptions({
      ...selectedOptions,
      pageNumber: 1,
      pageSize: option.value
    });
    getData({
      ...selectedOptions,
      auctionId: window.location.pathname.split('/')[3],
      pageNumber: 1,
      pageSize: option.value
    });
  };

  const onTypingFilterSearch = value => {
    const dataToSend = {
      ...selectedOptions,
      freeSearchString: value || selectedOptions.freeSearchString
    };
    setSelectedOptions(value);
    getData(dataToSend);
  };

  return {
    onSelectedFilter,
    onToggleFavorite,
    changeItemsPerPage,
    rangeOptions: getRangesOptions(),
    onTypingFilterSearch,
    selectedOptions,
    isSelectedFilters: isFilterSelected(selectedOptions),
    clearFilters,
    onSubmit,
    sortByHandler,
    filterFormData: { register, handleSubmit, errors, watch },
    dictionaryData: {
      jewelryTypes,
      metals,
      categories,
      gemstones,
      brandsAndDesigners,
      estimationRanges,
      styles
    },
    chosenFilters,
    isCatalog,
    isMyLots,
    isMyBids
  };
};
